import { ButtonType, CheckBoxWithLabelWidget } from '@tableau/widgets';
import { IQService } from 'angular';
import { IPromise } from 'q';
import React = require('react');
import _ = require('underscore');
import { Messages } from '../../codegen/Localize';
import { ButtonWithSpinner } from '../../common/react/components/ButtonWithSpinner';
import { SiteRolePickerMenu } from '../../common/react/components/SiteRolePickerMenu';
import { AngularContext } from '../../ts/factories/AngularContextFactory';
import { ISiteRoleFilter } from '../../ts/filters/SiteRoleFilter';
import { ResultHelper } from '../../ts/helpers/ResultHelper';
import { L10n } from '../../ts/L10n/L10n';
import { IGroupBasicInfo, IGroupInfo } from '../../ts/models/IGroupInfo';
import { Set } from '../../ts/models/Set';
import { Groups } from '../../ts/resources/Groups';
import { DefaultSiteRole, SiteRoleOption, SiteRoleOptionsResource } from '../../ts/resources/SiteRoleOptions';
import { GrantLicenseModes } from '../../ts/ServerApi/requests/SyncActiveDirectoryGroupRequest';
import { IResult } from '../../ts/ServerApi/types/IResponse';
import { SiteRoles } from '../../ts/ServerApi/types/ISiteRole';
import { ToasterService, ToastType } from '../../ts/services/ToasterService';
import { ServiceLocator } from '../ServiceLocator';
import { SiteRolePopover } from '../users/SiteRolePopover';

export interface UpdateGrantLicenseModeDialogState {
  readonly siteRole: string;
  readonly grantLicenseModeCheckboxChecked: boolean;
  readonly busy: boolean;
}

export interface UpdateGrantLicenseModeDialogProps {
  readonly close: (result: any) => void;
  readonly angularContext: AngularContext;
  readonly dismiss: () => void;
  readonly groupsToSet: Set<IGroupInfo>;
}

export class UpdateGrantLicenseModeDialog extends React.Component<
UpdateGrantLicenseModeDialogProps,
UpdateGrantLicenseModeDialogState
> {
  private $q = ServiceLocator.get<IQService>('$q');
  private translate = ServiceLocator.get<ng.translate.ITranslateService>('$translate');
  private siteRolesOptionResource: SiteRoleOptionsResource = ServiceLocator.get<SiteRoleOptionsResource>('SiteRoleOptionsResource');
  private siteRoleOptions = this.siteRolesOptionResource.getAllSiteRoleOptionsExcept(SiteRoles.ServerAdministrator);
  private CheckBoxContainerStyle: React.CSSProperties = {
    marginLeft: '0px', marginTop: '8px'
  };
  private Groups: Groups = ServiceLocator.get<Groups>('Groups');
  private toaster: ToasterService = ServiceLocator.get<ToasterService>('ToasterService');
  private $filter: ng.IFilterService = ServiceLocator.get<ng.IFilterService>('$filter');

  public constructor(props: UpdateGrantLicenseModeDialogProps) {
    super(props);
    let siteRole: string = DefaultSiteRole;
    let checkboxChecked: boolean = false;
    if (props.groupsToSet.size() === 1) {
      let group: IGroupInfo = props.groupsToSet.getByIndex(0);
      siteRole = group.minimumSiteRole && group.minimumSiteRole !== SiteRoles.Unlicensed ? group.minimumSiteRole : siteRole;
      checkboxChecked = group.grantLicenseMode === GrantLicenseModes.ON_LOGIN;
    }
    this.state = {
      siteRole: siteRole,
      grantLicenseModeCheckboxChecked: checkboxChecked,
      busy: false
    };
  }

  private readonly getSiteRoleOptionBySiteRoleCallback = (siteRole: string): SiteRoleOption => {
    return this.siteRolesOptionResource.getSiteRoleOptionBySiteRole(siteRole);
  }

  private readonly onOptionSelectedCallback = (siteRoleOption: SiteRoleOption): void => {
    this.onSiteRoleSelected(siteRoleOption);
  }

  public render() {
    return (
      <div>
        <div className="tb-dialog-title">{Messages.licenseUponLoginDialogTitle()}</div>
        <div className="tb-dialog-body-input-description">{Messages.confirmSetGroupsMinimumSiteRoleDesc({
            GROUP_NAME: L10n.elideIfLong(this.props.groupsToSet.first().name),
            GROUP_COUNT: this.props.groupsToSet.size(),
            GROUP_COUNT_LOC: L10n.Interpolations.Count
          })}</div>
        <div>
          <CheckBoxWithLabelWidget
            checked={this.state.grantLicenseModeCheckboxChecked}
            handleChange={this.handleCheckboxChange}
            containerStyle= {this.CheckBoxContainerStyle}
            label={Messages.grantLicenseUponLoginCheckboxLabel()}
            testId="lul-update-local-group"
          />
        </div>
        <div className="tb-select-role-dropdown-container tb-row-with-icon-on-right tb-margin-top-wide">
          <SiteRolePickerMenu
              translate={this.translate.instant}
              siteRoleOptions={this.siteRoleOptions}
              getSiteRoleOptionBySiteRole={this.getSiteRoleOptionBySiteRoleCallback}
              onOptionSelected={this.onOptionSelectedCallback}
              selectedRole={this.siteRolesOptionResource.getSiteRoleOptionBySiteRole(this.state.siteRole)}
              disabledRoleOptions={[this.siteRolesOptionResource.getSiteRoleOptionBySiteRole(SiteRoles.Unlicensed)]}
              isDisabled={!this.state.grantLicenseModeCheckboxChecked || this.state.busy}
            />
            <div className="tb-row-icon-on-right">
              <SiteRolePopover angularContext={this.props.angularContext}/>
            </div>
        </div>
        <div className="tb-dialog-actions">
          <ButtonWithSpinner
            buttonType={ButtonType.Outline}
            showSpinner={false}
            clicked={this.props.dismiss}
            testId={'confirm-action-dialog-cancel'}
            title={Messages.cancel()}
            disabled={this.state.busy}
          />
          <span className="tb-margin-left-wide">
            <ButtonWithSpinner
              buttonType={ButtonType.Go}
              showSpinner={this.state.busy}
              clicked={this.updateGrantLicenseModeCallback}
              testId={'confirm-action-dialog-submit'}
              title={Messages.confirm()}
              disabled={this.state.busy}
            />
          </span>
        </div>
      </div>
    );
  }

  private handleCheckboxChange = (checked: boolean) => {
    this.setState({grantLicenseModeCheckboxChecked: checked});
  }

  private updateGrantLicenseModeCallback = (): IPromise<any> => {
    this.setState({busy: true});
    const groups = _.map(this.props.groupsToSet.toArray(), (group: IGroupInfo) => group.id);
    const grantLicenseMode: GrantLicenseModes = this.state.grantLicenseModeCheckboxChecked ? GrantLicenseModes.ON_LOGIN : GrantLicenseModes.ON_SYNC;
    const siteRole = this.state.grantLicenseModeCheckboxChecked ? this.state.siteRole : SiteRoles.Unlicensed;

    return this.updateGroupsMinimumSiteRole(groups, siteRole, grantLicenseMode);
  }

  private handleSuccessResult(groups: Set<IGroupBasicInfo>, siteRole: string) {
    this.toaster.pop({
      type: ToastType.Info,
      text: Messages.updateGroupsMinimumSiteRoleSuccess({
        GROUP_NAME: L10n.elideIfLong(groups.first().name),
        COUNT: groups.size(),
        SITE_ROLE: this.$filter<ISiteRoleFilter>('siteRole')(siteRole),
        COUNT_LOC: L10n.Interpolations.Count
      })
    });
  }

  private handleErrorResult(groups: Set<IGroupBasicInfo>, siteRole: string, result: IResult) {
    this.toaster.pop({
      type: ToastType.Error,
      text: Messages.updateGroupsMinimumSiteRoleError({
        GROUP_NAME: L10n.elideIfLong(groups.first().name),
        COUNT: groups.size(),
        SITE_ROLE: this.$filter<ISiteRoleFilter>('siteRole')(siteRole),
        COUNT_LOC: L10n.Interpolations.Count
      })
    });
    return this.$q.reject(result); // for chaining errors
  }

  private onSiteRoleSelected(siteRoleOption: SiteRoleOption): void {
    this.setState({siteRole: siteRoleOption.role});
  }

  public updateGroupsMinimumSiteRole = (groups: string[], siteRole: string, grantLicenseMode: GrantLicenseModes): IPromise<any> => {
    return this.Groups.updateGroupsMinimumSiteRole(groups, siteRole, grantLicenseMode)
        .then(ResultHelper.rejectResultOnError(this.$q))
        .then(() => {
          this.handleSuccessResult(this.props.groupsToSet, siteRole);
          this.props.close({});
        })
        .catch((result: IResult) => this.handleErrorResult(this.props.groupsToSet, siteRole, result));
  }
}
